import React, { useState, useRef, useEffect } from 'react';
import { useDraggable } from '@dnd-kit/core';
import { StickyColor, STICKY_COLORS } from './CanvasToolbar';

export interface StickyNote {
  id: string;
  content: string;
  x: number;
  y: number;
  width: number;
  height: number;
  color: StickyColor;
  zIndex: number;
  is_author_hidden?: boolean;
  createdBy?: {
    id: string;
    display_name: string;
    initials: string;
    avatar_color: string;
  };
}

interface CanvasStickyNoteProps {
  sticky: StickyNote;
  isSelected: boolean;
  onSelect: (id: string) => void;
  onUpdate: (id: string, updates: Partial<StickyNote>) => void;
  onDelete: (id: string) => void;
  onToggleVisibility?: (id: string) => void;
  currentUserId?: string;
  zoom: number;
  autoEdit?: boolean;
  onEditEnd?: () => void;
}

const CanvasStickyNote: React.FC<CanvasStickyNoteProps> = ({
  sticky,
  isSelected,
  onSelect,
  onUpdate,
  onDelete,
  onToggleVisibility,
  currentUserId,
  zoom,
  autoEdit = false,
  onEditEnd,
}) => {
  const isAuthor = currentUserId && sticky.createdBy?.id === currentUserId;
  const isHidden = sticky.is_author_hidden || false;
  const [isEditing, setIsEditing] = useState(autoEdit);
  const [showColorPicker, setShowColorPicker] = useState(false);
  // LOCAL content state for smooth editing (no parent re-renders while typing)
  const [localContent, setLocalContent] = useState(sticky.content);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const colorPickerRef = useRef<HTMLDivElement>(null);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    isDragging,
  } = useDraggable({
    id: sticky.id,
    data: {
      type: 'sticky',
      sticky,
    },
  });

  // Auto edit when prop changes
  useEffect(() => {
    if (autoEdit) {
      setLocalContent(sticky.content);
      setIsEditing(true);
    }
  }, [autoEdit, sticky.content]);

  // Sync local content when sticky.content changes from outside (e.g., WebSocket update)
  useEffect(() => {
    if (!isEditing) {
      setLocalContent(sticky.content);
    }
  }, [sticky.content, isEditing]);

  // Close color picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (colorPickerRef.current && !colorPickerRef.current.contains(event.target as Node)) {
        setShowColorPicker(false);
      }
    };

    if (showColorPicker) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showColorPicker]);

  const handleDoubleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isHidden && !isAuthor) return;
    setLocalContent(sticky.content);
    setIsEditing(true);
  };

  const handleBlur = () => {
    setIsEditing(false);
    // Send content to parent ONLY on blur (not while typing)
    if (localContent !== sticky.content) {
      onUpdate(sticky.id, { content: localContent });
    }
    onEditEnd?.();
  };

  const handleContentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    // Update LOCAL state only - no parent update while typing
    setLocalContent(e.target.value);
  };

  const handleColorChange = (color: StickyColor) => {
    onUpdate(sticky.id, { color });
    setShowColorPicker(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsEditing(false);
    }
  };

  const isMaskedForViewer = isHidden && !isAuthor;

  const style: React.CSSProperties = {
    position: 'absolute',
    left: sticky.x,
    top: sticky.y,
    width: sticky.width,
    minHeight: sticky.height,
    backgroundColor: sticky.color.bg,
    borderColor: sticky.color.border,
    color: sticky.color.text,
    transform: transform ? `translate(${transform.x / zoom}px, ${transform.y / zoom}px)` : undefined,
    zIndex: isDragging ? 1000 : sticky.zIndex,
    cursor: isDragging ? 'grabbing' : isEditing ? 'text' : 'grab',
    opacity: isDragging ? 0.8 : isMaskedForViewer ? 0.75 : 1,
    boxShadow: isDragging 
      ? '0 25px 50px -12px rgba(0, 0, 0, 0.25)' 
      : isSelected 
        ? '0 10px 40px -10px rgba(0, 0, 0, 0.2)' 
        : '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`
        rounded-lg border-2 transition-shadow duration-200 select-none group
        ${isSelected ? 'ring-2 ring-orange-500 ring-offset-2' : ''}
        ${isDragging ? 'rotate-2 scale-105' : ''}
      `}
      onClick={(e) => {
        e.stopPropagation();
        onSelect(sticky.id);
      }}
      onDoubleClick={handleDoubleClick}
      {...(isEditing || isMaskedForViewer ? {} : { ...listeners, ...attributes })}
    >
      {/* Floating Toolbar - appears when selected */}
      {isSelected && !isDragging && (
        <div 
          className="absolute -top-14 left-1/2 -translate-x-1/2 flex items-center gap-1 bg-white rounded-xl shadow-xl border border-gray-200 p-1.5 z-50"
          style={{ transform: `translateX(-50%) scale(${1/zoom})` }}
        >
          {/* Color Button */}
          <div className="relative" ref={colorPickerRef}>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowColorPicker(!showColorPicker);
              }}
              className="w-8 h-8 rounded-lg border-2 hover:scale-110 transition-transform"
              style={{ 
                backgroundColor: sticky.color.bg, 
                borderColor: sticky.color.border 
              }}
              title="Change Color"
            />
            
            {/* Color Picker Dropdown */}
            {showColorPicker && (
              <div className="absolute top-full left-0 mt-2 p-3 bg-white rounded-xl shadow-2xl border border-gray-200 grid grid-cols-6 gap-1.5 z-50 animate-fade-in">
                {STICKY_COLORS.map((color, index) => (
                  <button
                    key={index}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleColorChange(color);
                    }}
                    className={`
                      w-7 h-7 rounded-md transition-all hover:scale-110 active:scale-95 border-2
                      ${sticky.color.bg === color.bg ? 'ring-2 ring-orange-500 ring-offset-1' : ''}
                    `}
                    style={{ 
                      backgroundColor: color.bg, 
                      borderColor: color.border 
                    }}
                    title={color.name}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Divider */}
          <div className="w-px h-6 bg-gray-200" />

          {/* Text Alignment */}
          <button
            className="p-2 rounded-lg hover:bg-gray-100 text-gray-600 transition-colors"
            title="Align Left"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h10M4 18h16" />
            </svg>
          </button>

          <button
            className="p-2 rounded-lg hover:bg-gray-100 text-gray-600 transition-colors"
            title="Bring to Front"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
            </svg>
          </button>

          {/* Hide/Show toggle — only for card author */}
          {isAuthor && onToggleVisibility && (
            <>
              <div className="w-px h-6 bg-gray-200" />
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleVisibility(sticky.id);
                }}
                className={`p-2 rounded-lg transition-colors ${isHidden ? 'bg-amber-100 text-amber-600 hover:bg-amber-200' : 'hover:bg-gray-100 text-gray-600'}`}
                title={isHidden ? 'Show to others' : 'Hide from others'}
              >
                {isHidden ? (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
            </>
          )}

          {/* Divider */}
          <div className="w-px h-6 bg-gray-200" />

          {/* Delete Button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete(sticky.id);
            }}
            className="p-2 rounded-lg hover:bg-red-100 text-gray-600 hover:text-red-600 transition-colors"
            title="Delete"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        </div>
      )}

      {/* Hidden badge — visible to everyone when card is author-hidden */}
      {isHidden && (
        <div className="absolute top-1 left-1 z-10 flex items-center gap-1 bg-amber-500 text-white text-xs font-semibold px-1.5 py-0.5 rounded">
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
          </svg>
          Hidden
        </div>
      )}

      {/* Content Area */}
      <div className={`p-4 h-full flex flex-col ${isHidden && isAuthor ? 'opacity-60' : ''}`}>
        {isEditing ? (
          <textarea
            ref={textareaRef}
            value={localContent}
            onChange={handleContentChange}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            className="flex-1 w-full bg-transparent resize-none outline-none text-lg font-medium leading-relaxed placeholder:text-gray-400"
            style={{ color: sticky.color.text }}
            placeholder="Write something..."
            autoFocus
          />
        ) : (
          <p 
            className={`flex-1 text-lg font-medium leading-relaxed whitespace-pre-wrap break-words ${isMaskedForViewer ? 'italic tracking-widest' : ''}`}
            style={{ color: sticky.color.text }}
          >
            {sticky.content || (
              <span className="opacity-50 italic">
                {isMaskedForViewer ? 'Hidden' : 'Double-click to edit...'}
              </span>
            )}
          </p>
        )}
      </div>

      {/* Resize Handle */}
      <div 
        className="absolute bottom-1 right-1 w-4 h-4 cursor-se-resize opacity-0 group-hover:opacity-50 transition-opacity"
        onMouseDown={(e) => {
          e.stopPropagation();
          // Resize logic would go here
        }}
      >
        <svg className="w-4 h-4 text-gray-400" viewBox="0 0 24 24" fill="currentColor">
          <path d="M22 22H20V20H22V22ZM22 18H20V16H22V18ZM18 22H16V20H18V22ZM22 14H20V12H22V14ZM18 18H16V16H18V18ZM14 22H12V20H14V22Z" />
        </svg>
      </div>

      {/* Selection indicator dots - corners */}
      {isSelected && (
        <>
          <div className="absolute -top-1.5 -left-1.5 w-3 h-3 bg-orange-500 rounded-full border-2 border-white shadow" />
          <div className="absolute -top-1.5 -right-1.5 w-3 h-3 bg-orange-500 rounded-full border-2 border-white shadow" />
          <div className="absolute -bottom-1.5 -left-1.5 w-3 h-3 bg-orange-500 rounded-full border-2 border-white shadow" />
          <div className="absolute -bottom-1.5 -right-1.5 w-3 h-3 bg-orange-500 rounded-full border-2 border-white shadow" />
        </>
      )}
    </div>
  );
};

export default CanvasStickyNote;

